import React, { useState, useEffect } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { FaUserPlus, FaCheck } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/class-settings-modals.css";

//Created By Cursor
const AddClassTeacher = ({ actionClose, selectedClass, sessionId }) => {
  const [loggedIn] = useState(localStorage.getItem("token") !== null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(true);
  const [teacherList, setTeacherList] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");

  useEffect(() => {
    fetchTeacherList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = (didSave = false) => {
    setShow(false);
    if (typeof actionClose === "function") {
      actionClose(didSave === true);
    }
  };

  //Created By Cursor
  const resolveSessionId = () =>
    selectedClass?.session_id ||
    sessionId ||
    SessionManager.getCurrentSession()?.[0]?.session_id ||
    "";

  //Created By Cursor
  const resolveSectionId = () => {
    const sid = selectedClass?.section_id;
    if (!sid || sid === "null" || String(sid).toLowerCase() === "null") {
      return "No";
    }
    return String(sid);
  };

  //Created By Cursor — load teachers; fall back to full staff list
  const fetchTeacherList = async () => {
    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const SESSION_ID = resolveSessionId();

    try {
      const teacherUrl = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getClassTeacherList.php`;
      const teacherRes = await axios.get(teacherUrl, {
        params: { school_code: SCHOOL_CODE, session_id: SESSION_ID },
      });

      let list = [];
      if (
        teacherRes.data?.api_status === "OK" &&
        Array.isArray(teacherRes.data.data) &&
        teacherRes.data.data.length > 0
      ) {
        list = teacherRes.data.data;
      } else {
        const staffUrl = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getStaff.php`;
        const staffRes = await axios.get(staffUrl, {
          params: { school_code: SCHOOL_CODE },
        });
        if (
          staffRes.data?.api_status === "OK" &&
          Array.isArray(staffRes.data.staff)
        ) {
          list = staffRes.data.staff;
        }
      }

      setTeacherList(list);
      if (list.length === 0) {
        myToast.notify_warning("No staff found to assign as class teacher");
      }

      // Preselect current teacher if present on the class card
      if (selectedClass?.class_teacher_id) {
        setSelectedTeacherId(String(selectedClass.class_teacher_id));
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      myToast.notify_error("Failed to load teachers");
      setTeacherList([]);
    } finally {
      setLoading(false);
    }
  };

  //Created By Cursor
  const handleClassTeacherSubmit = async () => {
    if (!selectedTeacherId) {
      myToast.notify_warning("Please select a class teacher");
      return;
    }
    if (!selectedClass?.class_id) {
      myToast.notify_warning("Class details missing");
      return;
    }

    const SESSION_ID = resolveSessionId();
    const SECTION_ID = resolveSectionId();
    if (!SESSION_ID) {
      myToast.notify_warning("Session is missing. Please reselect session.");
      return;
    }

    setSaving(true);
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateClassTeacher.php`;

      const params = {
        school_code: SCHOOL_CODE,
        staff_id: selectedTeacherId,
        section_id: SECTION_ID,
        class_id: selectedClass.class_id,
        session_id: SESSION_ID,
      };

      const response = await axios.get(URL, { params });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Class teacher updated successfully!");
        handleClose(true);
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Update failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Class teacher update error:", error);
      myToast.notify_error("An error occurred while updating class teacher.");
    } finally {
      setSaving(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const classLabel = selectedClass
    ? `${selectedClass.class_name}${
        selectedClass.section_name ? ` · ${selectedClass.section_name}` : ""
      }`
    : "";

  return (
    <Modal
      show={show}
      onHide={() => handleClose(false)}
      animation={false}
      size="md"
      centered
      dialogClassName="cls-mdl-root"
      aria-labelledby="add-teacher-title"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title id="add-teacher-title">
            <FaUserPlus />
            Class Teacher
          </Modal.Title>
          <span className="cls-mdl-sub">
            Assign a class teacher to this class/section
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <MyLoader />
        ) : (
          <div className="cls-mdl-panel">
            {classLabel ? (
              <div className="cls-mdl-chip">
                <i className="fa fa-graduation-cap" aria-hidden="true" />
                {classLabel}
              </div>
            ) : null}

            <label className="cls-mdl-field full">
              <span>Class Teacher *</span>
              <select
                required
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
                disabled={saving}
              >
                <option value="">Select Class Teacher</option>
                {teacherList.map((item) => (
                  <option key={item.staff_id} value={item.staff_id}>
                    {item.staff_name}
                    {item.staff_role ? ` (${item.staff_role})` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          className="cls-mdl-btn-ghost"
          onClick={() => handleClose(false)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="cls-mdl-btn-cta"
          onClick={handleClassTeacherSubmit}
          disabled={loading || saving || !selectedTeacherId}
        >
          <FaCheck style={{ marginRight: 6 }} />
          {saving ? "Saving..." : "Save Teacher"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddClassTeacher;
